import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';
import styled from 'styled-components';

import type { SelectOption } from '../types';
import { fieldControlCss, selectControlStyle } from './field-control-style';

type SelectFieldProps = {
  field: { label?: string; labelIcon?: React.ReactNode };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

const hintStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: '#646d72',
};

/**
 * Keeps the behavioral safeguards of our custom select while matching Puck's native field
 * dimensions, radius, colors, and interaction states. The white fill is intentionally independent
 * of the muted inspector sidebar so controls remain easy to scan.
 */
const StyledSelect = styled.select`
  ${fieldControlCss}
  ${selectControlStyle}

  &:hover:not(:disabled) {
    background-color: var(
      --dcm-cms-select-hover-bg,
      var(--puck-field-color-bg-hover, var(--puck-color-interactive-soft-hover, #f7fbff))
    );
    border-color: var(
      --dcm-cms-select-hover-border-color,
      var(--puck-field-color-border-hover, var(--puck-color-border-hover, #0b99e6))
    );
    color: var(
      --dcm-cms-select-hover-text-color,
      var(--puck-field-color-text-hover, var(--puck-field-color-text, var(--puck-color-text, #121516)))
    );
  }
`;

/**
 * Returns a Puck custom field render function for enumerated CSS values.
 * The `options` parameter defines the available choices (e.g. font weights, text transforms).
 *
 * `hint`, when provided, is rendered as small muted helper text beneath the `<select>` and wired
 * up via `aria-describedby` -- this is deliberately separate from `ai.instructions` (below in
 * `selectField()`). The LLM copy explains how to *set* the field correctly from a prompt; the
 * human copy explains what the control *does* to a non-technical retail-staff author looking at
 * a bare dropdown (e.g. "Only meaningful when Aspect ratio isn't Original"). Conflating the two
 * would mean either over-explaining to the AI or under-explaining to the author.
 */
export function SelectField(options: readonly SelectOption[], hint?: string) {
  return function SelectFieldWidget({ field, name, value, onChange, readOnly }: SelectFieldProps): JSX.Element {
    const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.currentTarget.value), [
      onChange,
    ]);
    const hintId = hint ? `select-hint-${name}` : undefined;
    // Puck's custom-field value is `any` at runtime. Keep the native select controlled when an
    // absent nested prop reaches the widget despite this field's declared string boundary.
    const normalizedValue = value ?? '';

    // Saved page data is unvalidated JSON, so the stored value may not be one of the current options
    // -- content authored before an option existed, a renamed key, or a component that deliberately
    // accepts a raw CSS value alongside its presets (Section's max width). A <select> with no
    // matching <option> silently displays the *first* one, so the control would assert a value the
    // page is not actually using. Surfacing it keeps the panel honest, and leaves switching to a
    // preset an explicit choice rather than a side effect of opening the panel.
    const isUnlisted = normalizedValue !== '' && !options.some((opt) => opt.value === normalizedValue);

    // A component whose `defaultProps` leave this field empty (e.g. Product Carousel's
    // `category: ''`) has no matching <option>, and a <select> with no matching option
    // displays the *first* one. The panel would then show "Flower" while the stored value
    // is still '', so the renderer sees unconfigured and draws nothing -- and picking the
    // already-displayed "Flower" fires no change event, so the author has to select away
    // and back to get a row. An explicit empty option keeps the control honest about the
    // absence of a choice, and makes the first real selection a genuine change.
    const needsEmptyOption = normalizedValue === '' && !options.some((opt) => opt.value === '');

    return (
      <FieldLabel
        label={field.label ?? name}
        icon={field.labelIcon}
        readOnly={readOnly}
        el={field.labelIcon ? 'div' : 'label'}
      >
        <StyledSelect
          value={normalizedValue}
          aria-label={field.labelIcon ? field.label ?? name : undefined}
          onChange={handleChange}
          disabled={readOnly}
          data-testid={`select-${name}`}
          aria-describedby={hintId}
        >
          {needsEmptyOption && <option value=''>Select…</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {isUnlisted && <option value={normalizedValue}>{`Custom (${normalizedValue})`}</option>}
        </StyledSelect>
        {hint && (
          <p id={hintId} data-testid={`select-hint-${name}`} style={hintStyle}>
            {hint}
          </p>
        )}
      </FieldLabel>
    );
  };
}

/**
 * Builds a complete Puck `custom` select field, including the `ai.schema` the AI plugin
 * requires for every `custom` field.
 *
 * A bare `{ type: 'custom', render: SelectField(options) }` without `ai.schema` 400s the entire
 * AI chat request ("Missing schema for ... with custom field type"). Deriving the schema's enum
 * from `options` here means call sites cannot omit it.
 *
 * `key` is what makes a `resolveFields`-rebuilt field actually re-render. Puck memoises the
 * component it draws for a custom field on `[field.type, field.key, render]`
 * (`@puckeditor/core/dist/index.js` ~8198-8208). `type` is always `'custom'`, so without a `key`
 * the dep array is identical between resolves and Puck keeps the *previous* `render` closure --
 * which captured the *previous* `options`. The field's `ai.schema` updates (it is read from the
 * object, not the closure) while the visible dropdown keeps the stale list, so a dependent select
 * like Product Carousel's Subcategory silently shows the prior category's options. Deriving the
 * key from the option values means any change to the option list produces a new key.
 */
export function selectField(
  label: string,
  options: readonly SelectOption[],
  aiInstructions?: string,
  hint?: string
): CustomField<string> {
  const optionValues = options.map((option) => option.value);
  // An `enum` with no members is not a schema anything can satisfy -- Puck AI rejects it as
  // invalid JSON Schema outright, 400ing the whole AI chat request the moment it reaches this
  // field. This is a real, reachable state: `resolveFields`-produced fields such as Product
  // Carousel's Subcategory start with an empty option list until the author picks a Category,
  // and the field still needs *some* `ai.schema` in the meantime. Falling back to a bare
  // `{ type: 'string' }` describes the field honestly ("a string value", options TBD) instead of
  // asserting a constraint no value could ever meet.
  const schema =
    optionValues.length > 0 ? { type: 'string' as const, enum: optionValues } : { type: 'string' as const };
  return {
    type: 'custom',
    label,
    key: `select-${optionValues.join('|')}`,
    render: SelectField(options, hint),
    ai: {
      schema,
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
